/**
 * Iframe Protocol Types
 *
 * Type-safe postMessage protocol for Studio (host) <-> iframe (preview) communication.
 * All message types are prefixed with `IFRAME:` to avoid collision with the
 * page editor protocol.
 */

// ---------------------------------------------------------------------------
// Wire format
// ---------------------------------------------------------------------------

export interface IframeMessage {
  type: string
  id?: string
  payload?: Record<string, unknown>
  result?: { ok: boolean; error?: string }
}

export type IframeMessageHandler = (message: IframeMessage) => void

// ---------------------------------------------------------------------------
// Commands: Studio (host) -> iframe
// ---------------------------------------------------------------------------

export const IFRAME_COMMANDS = {
  LOAD_APP: 'IFRAME:LOAD_APP',
  NAVIGATE: 'IFRAME:NAVIGATE',
  SET_VIEWPORT: 'IFRAME:SET_VIEWPORT',
  MCP_EXECUTE: 'IFRAME:MCP_EXECUTE',
} as const

export type IframeCommandType = (typeof IFRAME_COMMANDS)[keyof typeof IFRAME_COMMANDS]

// ---------------------------------------------------------------------------
// Events: iframe -> Studio (host)
// ---------------------------------------------------------------------------

export const IFRAME_EVENTS = {
  APP_READY: 'IFRAME:APP_READY',
  ROUTE_CHANGED: 'IFRAME:ROUTE_CHANGED',
  ELEMENT_SELECTED: 'IFRAME:ELEMENT_SELECTED',
  STATE_UPDATE: 'IFRAME:STATE_UPDATE',
  STATE_SNAPSHOT: 'IFRAME:STATE_SNAPSHOT',
  FLOW_EXECUTED: 'IFRAME:FLOW_EXECUTED',
  EVENT_FIRED: 'IFRAME:EVENT_FIRED',
  MCP_RESULT: 'IFRAME:MCP_RESULT',
  NETWORK_REQUEST: 'IFRAME:NETWORK_REQUEST',
} as const

export type IframeEventType = (typeof IFRAME_EVENTS)[keyof typeof IFRAME_EVENTS]

// ---------------------------------------------------------------------------
// Typed payloads
// ---------------------------------------------------------------------------

/**
 * Resolved theme v2 contract — structural mirror of `ResolvedTheme` from
 * `@cogo/design-presets`. Duplicated here so iframe-protocol stays free of
 * a design-presets dep edge (the iframe message contract is the boundary).
 *
 * `atomProps` is intentionally typed as `Record<string, unknown>` — the
 * canonical shape (`Record<string, AtomDefaults>`) lives in design-presets
 * and lacks a string index signature, so we keep the protocol-side contract
 * permissive and let consumers re-cast through `ResolvedTheme` if they need
 * the strict shape.
 */
export interface ResolvedThemeLike {
  cssVars: Record<string, string>
  atomProps: Record<string, unknown>
  narrative?: unknown
  i18n?: unknown
}

/**
 * Wire-safe RenderPack bytes. Studio/publish payloads encode RPK1 bytes as
 * base64, then iframe transport carries them as number arrays so structured
 * clone and JSON-oriented tests have one deterministic shape. Runtime callers
 * convert these arrays back to Uint8Array before passing them into AppV2.
 */
export type RenderPackBytesPayload = Record<string, number[]>

export interface RendererThemeTokensPayload {
  colors?: Record<string, string>
  /**
   * Pass-through CSS custom properties — the app's own `--`-prefixed variable
   * names, emitted verbatim by `getIsolatedAppStyle()`. Declared here because
   * this payload is a hand-restated copy of the token surface: the record
   * already crosses the iframe by structured clone, so omitting the field
   * would leave the wire contract claiming a shape narrower than what travels,
   * and would reject a literal that named it.
   */
  cssVars?: Record<string, string>
  spacing?: Record<string, string | number>
  typography?: Record<string, {
    size?: string | number
    weight?: string | number
    lineHeight?: string | number
    family?: string
  }>
  radius?: Record<string, string | number> | string
  shadow?: Record<string, string>
  fonts?: { sans?: string; mono?: string; heading?: string }
  '--cogo-font-heading-url'?: string
  '--cogo-font-body-url'?: string
  fontFamily?: string
  fontWeightDefault?: number
}

export interface LoadAppPayload {
  manifest: Record<string, unknown>
  blocks: Record<string, unknown>
  designSystem?: Record<string, string>
  themeTokens?: RendererThemeTokensPayload
  /** Phase M1 Task 8: resolved theme v2 alongside legacy themeTokens. */
  theme?: ResolvedThemeLike
  /** Phase 396 / M1 Task 8: 6-axis Style name (lyra/vega/...). */
  styleName?: string
  /** Phase 396 / M1 Task 8: 6-axis Font name (geist/inter/...). */
  fontName?: string
  /** Standalone preview/app color scheme. Defaults to light in the renderer. */
  colorScheme?: 'light' | 'dark'
  /** Block-name keyed RPK1 bytes in wire-safe number-array form. */
  renderPackBytes?: RenderPackBytesPayload
  initialRoute?: string
}

export interface AppReadyPayload {
  appId: string
  appName: string
}

export interface NavigatePayload {
  route: string
}

export interface SetViewportPayload {
  width: number
  height: number
}

export interface RouteChangedPayload {
  route: string
}

export interface ElementSelectedPayload {
  elementId: string
  blockId: string
  elementType: string
}

// ---------------------------------------------------------------------------
// DevTools bridge payloads (iframe -> host)
// ---------------------------------------------------------------------------

export interface StateSnapshotPayload {
  snapshot: Record<string, unknown>
}

export interface FlowExecutedPayload {
  flowName: string
  duration: number
  stepCount: number
  success: boolean
  error?: string
}

export interface EventFiredPayload {
  elementId: string
  eventType: string
  flowName?: string
}

// ---------------------------------------------------------------------------
// MCP bridge payloads (host <-> iframe)
// ---------------------------------------------------------------------------

/** Payload sent from host to iframe via MCP_EXECUTE command. */
export interface McpExecutePayload {
  id: string // correlation ID for request/response matching
  tool: string // e.g. 'cogo.runtime.executeFlow'
  params: Record<string, unknown>
}

/** Payload sent from iframe to host when a network request completes. */
export interface NetworkRequestPayload {
  method: string        // GET, POST, PATCH, DELETE
  url: string           // full URL or path
  status: number        // HTTP status code
  duration: number      // ms
  requestBody?: unknown // request payload (if POST/PATCH)
  responseBody?: unknown // response data (truncated if large)
  error?: string        // network error message
}

/** Payload sent from iframe to host via MCP_RESULT event. */
export interface McpResultPayload {
  id: string // matches the request id
  success: boolean
  data?: unknown
  error?: string
}
